/**
 * EntityProfile Page
 * Thin rendering shell — logic lives in useBusinessProfile hook.
 */
import { ArrowLeft } from 'lucide-react';
import Footer from '../components/footer/Footer';
import { useBusinessProfile } from '../hooks/useBusinessProfile.js';
import { useParams } from 'react-router-dom';
import { storageService } from '../domain/services/StorageService.js';
import {
  LoadingScreen, ErrorScreen, HeroSection, AboutSection,
  PhotoGalleryCard, LocationAccessCard, EmergencyContactsCard, OpeningHoursCard,
  SocialLinksCard, RelatedBusinesses, GalleryModal, QRPlateModal, ImagePreviewModal
} from '../components/profile/SharedProfileComponents.jsx';
import MealScheduleCard from '../components/profile/MealScheduleCard.jsx';
import AssetsSection from '../components/entity-profile/AssetsSection.jsx';
import BannersSection from '../components/entity-profile/BannersSection.jsx';
import RatingsSection from '../components/entity-profile/RatingsSection.jsx';
import WeatherCard from '../components/profile/WeatherCard.jsx';
import HostelRoomsSection from '../components/hostel/HostelRoomsSection.jsx';

const EntityProfile = () => {
  const { tag } = useParams();
  const vm = useBusinessProfile(tag, 'tag');

  if (vm.loading) return <LoadingScreen />;
  if (vm.error || !vm.business) return <ErrorScreen error={vm.error} navigate={vm.navigate} />;

  // DO NOT redirect to MyBusinessProfile - always show public entity view
  // Users should access MyBusinessProfile only through Dashboard > My Businesses

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <button
          onClick={vm.handleBack}
          className="flex items-center gap-2 text-green-400 hover:text-green-300 font-medium transition-colors mb-6"
        >
          <ArrowLeft className="w-5 h-5" />
          {storageService.isAuthenticated() ? 'Back to My Businesses' : 'Back to Map'}
        </button>

        <HeroSection vm={vm} />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8 lg:items-start">
          <div className="lg:col-span-2 space-y-6">
            <AboutSection vm={vm} />
            {vm.banners.length > 0 && <BannersSection banners={vm.banners} />}
            {/* {vm.assets.length > 0 && <AssetsSection assets={vm.assets} />} */}
            <PhotoGalleryCard vm={vm} />
            <OpeningHoursCard business={vm.business} workingHours={vm.workingHours} isCurrentlyOpen={vm.isCurrentlyOpen} convertTo12Hour={vm.convertTo12Hour} />
            <MealScheduleCard business={vm.business} />
            {/* Show hostel rooms section for hostel category */}
            {(vm.business.category?.name?.toLowerCase().includes('hostel') || 
              vm.business.categories?.some(c => c.name?.toLowerCase().includes('hostel'))) && (
              <HostelRoomsSection hostelId={vm.business.id} hostelName={vm.business.name} />
            )}
          </div>

          <div className="space-y-6 lg:sticky lg:top-8">
            <LocationAccessCard business={vm.business} />
            <WeatherCard weather={vm.weather} />
            <EmergencyContactsCard category={vm.business.category?.name} />
            {vm.ratings.length > 0 && <RatingsSection ratings={vm.ratings} />}
            <SocialLinksCard socialLinks={vm.socialLinks} />
          </div>
        </div>

        <RelatedBusinesses businesses={vm.relatedBusinesses} navigate={vm.navigate} />
      </div>

      <GalleryModal vm={vm} />
      <QRPlateModal vm={vm} />
      <ImagePreviewModal showImagePreview={vm.showImagePreview} setShowImagePreview={vm.setShowImagePreview} previewImage={vm.previewImage} />
      <Footer />
    </div>
  );
};

export default EntityProfile;
